import { Airgap } from '@/components/app/consent_management/airgap.types';

export const CONSENT_CHANGE_EVENT = 'consent-change';

export const READY_TIMEOUT_MS = 10000;

const CONSENT_MANAGER_ELEMENT_ID = 'transcend-consent-manager';

// Airgap authorises setConsent with a trusted user interaction and ignores the call without one.
// Which types it accepts is Airgap's business: these are the ones a user produces, and the caller
// retries when Airgap turns one down.
const INTERACTION_EVENT_TYPES = [
  'pointerdown',
  'mousedown',
  'touchstart',
  'keydown',
  'wheel',
  'click',
];

const LISTENER_OPTIONS: AddEventListenerOptions = { capture: true, passive: true };

// consent_management_tag renders a parser-blocking script ahead of this deferred module, so a
// missing window.airgap means Transcend never ran. The timeout covers an init that threw and left
// a stub whose ready() never fires; the getConsent check covers a stub whose ready() somehow does.
export const whenAirgapReady = (): Promise<Airgap | null> => {
  const airgap = window.airgap;
  if (!airgap) return Promise.resolve(null);

  return new Promise((resolve) => {
    const timeout = window.setTimeout(() => resolve(null), READY_TIMEOUT_MS);
    const settle = (value: Airgap | null) => {
      window.clearTimeout(timeout);
      resolve(value);
    };

    try {
      airgap.ready(() => settle(typeof airgap.getConsent === 'function' ? airgap : null));
    } catch {
      settle(null);
    }
  });
};

const isFromConsentManager = (event: Event): boolean => {
  const manager = document.getElementById(CONSENT_MANAGER_ELEMENT_ID);

  return !!manager && event.target instanceof Node && manager.contains(event.target);
};

export type InteractionEventSource = {
  // Resolves null when the wait is released without an interaction, so a caller can finish instead
  // of holding its listeners for the rest of the page.
  next: () => Promise<Event | null>;
  abort: () => void;
  stop: () => void;
};

// Hands out user interactions one at a time. Airgap accepts an interaction long after it fired and
// accepts the same one twice, so holding one is enough to authorise a restore whenever the database
// read resolves.
export const createInteractionEventSource = (): InteractionEventSource => {
  let pending: Event | null = null;
  let aborted = false;
  let resolveNext: ((event: Event | null) => void) | null = null;

  const release = () => {
    aborted = true;
    const resolve = resolveNext;
    resolveNext = null;
    if (resolve) resolve(null);
  };

  const handle = (event: Event) => {
    // An interaction with the Transcend banner is the user answering it. Restoring on the back of
    // that click would overwrite the choice they are making.
    if (isFromConsentManager(event)) return;

    if (!resolveNext) {
      pending = event;
      return;
    }

    const resolve = resolveNext;
    resolveNext = null;
    resolve(event);
  };

  INTERACTION_EVENT_TYPES.forEach((type) =>
    document.addEventListener(type, handle, LISTENER_OPTIONS)
  );

  return {
    // Each interaction is handed out once, so a caller retrying after a rejected setConsent waits
    // for a new one instead of replaying the interaction Airgap just refused.
    next: () => {
      // The abort can land before the caller comes back to wait — while the database read is still
      // in flight, say — so it latches instead of relying on someone being parked at that moment.
      if (aborted) return Promise.resolve(null);

      if (pending) {
        const event = pending;
        pending = null;

        return Promise.resolve(event);
      }

      return new Promise<Event | null>((resolve) => {
        resolveNext = resolve;
      });
    },
    // Answering the banner leaves nothing to authorise. Without this, a user who touches only the
    // banner parks the restore on next() forever and its listeners outlive the sync.
    abort: release,
    stop: () => {
      release();
      INTERACTION_EVENT_TYPES.forEach((type) =>
        document.removeEventListener(type, handle, LISTENER_OPTIONS)
      );
    },
  };
};
